import React, { useEffect } from 'react';

/*
 * ToolShell — shared chrome for every public lead-magnet tool under /tools/*.
 *
 * THE FACTORY: a new free SEO tool = one page component that renders its inputs
 * + output inside <ToolShell>. The shell supplies the identical header, hero,
 * SEO <title>/<meta>, dark conversion CTA, footer, and responsive 2-column grid
 * so each new tool is just: config + fields + calculation. See RateCardToolPage
 * and YtMoneyToolPage for the two reference implementations.
 *
 * Fully client-side (no backend, no auth). SEO meta is set on mount; for real
 * ranking + correct social previews this pairs with build-time prerendering
 * (react-snap) — see SEO_TOOLS.md.
 */

export const ACCENT = '#1a7a4a';
export const INK = '#0f1117';
export const SIGNUP_URL = '/?auth=signup';
const SITE = 'https://creatorkhata.com';

export const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
export const roundTo = (x, step) => Math.max(step, Math.round(x / step) * step);

/** Open a print-ready HTML document in a new tab (user prints / saves as PDF). */
export function printDoc(html) {
  const w = window.open('', '_blank');
  if (!w) { alert('Please allow pop-ups to download your document.'); return; }
  w.document.write(html);
  w.document.close();
}

/** Set document <title> + <meta name=description> + canonical for this tool. */
function useToolSeo({ title, description, path }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta'); m.name = 'description';
      document.head.appendChild(m); return m;
    })();
    const prevDesc = meta.getAttribute('content');
    if (description) meta.setAttribute('content', description);

    let canon = document.querySelector('link[rel="canonical"]');
    const createdCanon = !canon;
    if (!canon) { canon = document.createElement('link'); canon.rel = 'canonical'; document.head.appendChild(canon); }
    const prevCanon = canon.getAttribute('href');
    if (path) canon.setAttribute('href', SITE + path);

    return () => {
      document.title = prevTitle;
      if (prevDesc) meta.setAttribute('content', prevDesc);
      if (createdCanon) canon.remove();
      else if (prevCanon) canon.setAttribute('href', prevCanon);
    };
  }, [title, description, path]);
}

/**
 * Props:
 *  - seo: { title, description, path }   (path e.g. '/tools/youtube-money-calculator')
 *  - h1, sub:  hero heading + subheading
 *  - cta:      { heading, body } for the dark conversion band (optional)
 *  - children: the tool body (rendered inside the responsive 2-col grid)
 */
export default function ToolShell({ seo, h1, sub, cta, children }) {
  useToolSeo(seo);

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fb', fontFamily: "'DM Sans', sans-serif", color: INK }}>
      {/* Header */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', maxWidth: 1080, margin: '0 auto' }}>
        <a href="/" style={{ textDecoration: 'none', color: INK, fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.15rem' }}>
          <span style={{ color: ACCENT }}>●</span> CreatorKhata
        </a>
        <a href={SIGNUP_URL} style={{ ...btn(ACCENT), textDecoration: 'none' }}>Try CreatorKhata free →</a>
      </header>

      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '1.5rem 1.25rem 0.5rem', maxWidth: 760, margin: '0 auto' }}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '2rem', letterSpacing: '-0.02em', margin: '0 0 0.5rem' }}>{h1}</h1>
        <p style={{ color: '#666', fontSize: '1rem', margin: 0 }}>{sub}</p>
      </div>

      {/* Body — responsive 2-col grid (inputs | output) */}
      <div className="rct-grid"
        style={{ maxWidth: 1080, margin: '0 auto', padding: '1.5rem 1.25rem 3rem', display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: '1.5rem', alignItems: 'start' }}>
        {children}
      </div>

      {/* Conversion CTA */}
      <div style={{ background: INK, color: 'white', padding: '2.5rem 1.25rem', textAlign: 'center' }}>
        <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.5rem', margin: '0 0 0.5rem' }}>
          {cta?.heading || 'Your numbers are just the start.'}
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.8)', maxWidth: 560, margin: '0 auto 1.25rem', fontSize: '0.95rem' }}>
          {cta?.body || 'CreatorKhata manages your entire brand-deal flow — pitch, contract, invoice, and payment tracking — in one place. Stop leaving money on the table.'}
        </p>
        <a href={SIGNUP_URL} style={{ ...btn('white'), color: INK, textDecoration: 'none', fontSize: '1rem', padding: '0.8rem 1.6rem' }}>
          Try CreatorKhata free — PRO included till August →
        </a>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', fontSize: '0.75rem' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT, display: 'inline-block', marginRight: 6 }} />
        Made with CreatorKhata • <a href="/" style={{ color: '#94a3b8' }}>creatorkhata.com</a>
      </div>

      <style>{`@media (max-width: 760px){ .rct-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

/* ---- shared form primitives (reused by every tool) ---- */

export function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 700, color: '#444', marginBottom: '0.9rem' }}>
      {label}
      {children}
    </label>
  );
}

export const input = { padding: '0.6rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', background: 'white', width: '100%', boxSizing: 'border-box' };
export const card = () => ({ background: 'white', borderRadius: 16, padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' });
export const btn = (bg) => ({ background: bg, color: bg === 'white' ? INK : 'white', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' });
